/**
 * @module cart-drawer
 * @description Cart drawer entry point — slide-in cart UI loaded on every page.
 *
 * Extends BaseDrawer to render cart contents with morph-based updates.
 * Listens to CART_EVENTS for real-time state sync across tabs/components.
 *
 * Public API: dispatches cart-drawer:open / cart-drawer:close CustomEvents.
 *
 * @see {@link @stardust/cart} — core cart state + Shopify API
 * @see {@link @stardust/base-drawer} — drawer lifecycle base class
 */
import { morph, MORPH_OPTIONS } from "@stardust/morph";
import Cart, { CART_EVENTS } from "@stardust/cart";
import AddToCart from "@stardust/add-to-cart";
import BaseDrawer from "@stardust/base-drawer";
import { formatMoney, escapeHtml } from "@stardust/utilities";
import {
  buildAddonListHtml,
  getProductAddonsShowSwatchesSetting,
  parseProductFromAddonRow,
  resolveAddonVariant,
  applyAddonVariantToRow,
  sanitizeAddonProductList,
} from "@stardust/product-addon-row";
/**
 * Importmap alias for the active recommendation engine, resolved by snippets/scripts.liquid — the
 * per-brand swap happens there, so this line never changes. Imported lazily in
 * _renderAddonsFromContext, never statically: this module loads on every page, so a top-level import
 * would pull the engine's whole dependency tree into every page load and hard-fail the entire drawer
 * whenever the alias is absent.
 */
const ADDON_ENGINE = "@stardust/addon-engine";

/**
 * @param {object|null|undefined} ctx
 * @returns {boolean}
 */
function addonContextRenderable(ctx) {
  if (!ctx) return false;
  // Source-agnostic: any engine context that carries enough to render — an embedded product list
  // (metafields), a SearchSpring bundle tag, or a productId (API engines).
  if (Array.isArray(ctx.products) && ctx.products.length) return true;
  if (typeof ctx.tag === "string" && ctx.tag.trim()) return true;
  return Boolean(ctx.productId);
}

// ── Addon context persistence (sessionStorage) ─────────────────────
const ADDON_CTX_SESSION_KEY = "stardust:cart-drawer-addon-ctx";

function readPersistedAddonContext() {
  try {
    const raw = sessionStorage.getItem(ADDON_CTX_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persistAddonContext(ctx) {
  if (!addonContextRenderable(ctx)) return;
  try {
    sessionStorage.setItem(ADDON_CTX_SESSION_KEY, JSON.stringify(ctx));
  } catch (err) {
    console.warn("[CartDrawer] Could not persist addon context:", err);
  }
}

function clearPersistedAddonContext() {
  try {
    sessionStorage.removeItem(ADDON_CTX_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object} ctx
 * @param {{ items?: { product_id: number }[] }|null|undefined} cartData
 */
function persistedContextAppliesToCart(ctx, cartData) {
  if (!cartData?.items?.length) return false;
  const anchor = String(ctx.productId || "").trim();
  if (!anchor) return true;
  return cartData.items.some((line) => String(line.product_id) === anchor);
}

const CART_DRAWER_EVENTS = {
  open: "cart-drawer:open",
  close: "cart-drawer:close",
};

const ADDON_SECTION_SEL = "[data-cart-drawer-addons-section]";
const ADDON_CONTAINER_SEL = "[data-cart-drawer-addons]";
const CHECKOUT_SLOT_SEL = "[data-cart-drawer-checkout]";
const ITEMS_CONTAINER_SEL = "[cart-items-container]";
const CART_ITEMS_SEL = "[data-cart-items]";
const SCROLL_AREA_SEL = ".cart-drawer__content";

/**
 * Cart Drawer — slide-in cart UI with morph-based updates.
 * Features: sticky summary, installments close, lazy hydration,
 * optimistic items, addon recommendations.
 */
class CartDrawer extends BaseDrawer {
  /** @type {number} If summary/drawer height ratio exceeds this, summary becomes static */
  _summaryThreshold = 0.5;

  constructor(element) {
    super(element, {
      openClass: "opacity-0 translate-x-full",
      closeClass: "opacity-0 translate-x-full",
    });
    this.cart = null;
    this._pendingAddonContext = null;
    this._addonRenderGen = 0;
    this._refreshGen = 0;
    this._addInFlight = false;
  }

  // ── Lifecycle ────────────────────────────────────────────────────

  load() {
    this._isLazy = this.element.hasAttribute('data-lazy-cart-drawer');
    this._hydrated = !this._isLazy;
    this._hydratePromise = null;

    this._fetchInitialCartCount();
    this._bindEvents();

    if (!this._isLazy) {
      this._initCartInstance();
    } else {
      // Hydrate eagerly in the background so content is ready when the user
      // clicks open (avoids a 1-2s blank-shell wait on first open).
      this._hydrateDrawer();
    }
  }

  /**
   * Bind all document-level event listeners.
   */
  _bindEvents() {
    document.addEventListener(CART_DRAWER_EVENTS.open, (e) => this._onOpenEvent(e));
    document.addEventListener(CART_DRAWER_EVENTS.close, this._boundClose);
    document.addEventListener(CART_EVENTS.updated, (e) => this._onCartUpdated(e.detail));
    document.addEventListener(CART_EVENTS.addStart, (e) => this._onAddStart(e.detail));
    document.addEventListener(CART_EVENTS.refreshDrawer, (e) => this._refreshDrawerHTML(e.detail));
    document.addEventListener(CART_EVENTS.addFailed, (e) => this._onAddFailed(e.detail));

  }

  _onOpenEvent(e) {
    if (this.isOpen) {
      if (e.detail?.fromAddToCart) {
        if (addonContextRenderable(e.detail.addonContext)) {
          void this._renderAddonsFromContext(e.detail.addonContext);
        }
        return;
      }
      this.close();
      return;
    }

    if (!this._hydrated) this._hydrateDrawer();
    this._openWithContext(e);
  }

  _onCartUpdated(cartData) {
    if (cartData && this.cart) this.cart.cart = cartData;
    this._updateCartUI(cartData);

    if (this.isOpen && cartData?.item_count === 0 && !this._addInFlight) {
      this.close();
      return;
    }

    this._tryRestorePersistedAddons(cartData);

    requestAnimationFrame(() => this._updateStickyState());
  }

  _onAddStart(detail) {
    this._addInFlight = true;
    // Lock quantity/remove controls while the add is in flight — a
    // cart/change.js sent mid-add can land after the add re-splits lines
    // and hit the wrong index. Unlocked by the refresh finally or _onAddFailed.
    this.cart?._setAllItemsLoading(true);
    const { variantId, optimisticData } = detail ?? {};
    if (variantId == null) return;

    if (optimisticData) {
      this._showOptimisticItem(optimisticData);
    } else {
      this._showSkeleton(variantId);
    }
  }

  _onAddFailed(detail) {
    this._addInFlight = false;
    this.cart?._setAllItemsLoading(false);
    this._removeOptimisticItem(detail?.variantId);
  }

  /**
   * Open the drawer with context from the dispatched event.
   * @param {CustomEvent} e
   */
  _openWithContext(e) {
    this.open();

    if (e.detail?.ariaLiveText) {
      const el = this.element.querySelector("[data-cart-drawer-aria-live-text]");
      if (el) el.textContent = e.detail.ariaLiveText;
    }

    const addonCtx = addonContextRenderable(e.detail?.addonContext)
      ? e.detail.addonContext
      : this._pendingAddonContext;
    if (addonContextRenderable(addonCtx)) void this._renderAddonsFromContext(addonCtx);

    requestAnimationFrame(() => {
      this._updateStickyState();
      this._setupInstallmentsClose();
    });
  }

  /** BaseDrawer lifecycle hook — hide addons when drawer closes. */
  onClose() {
    this._setAddonVisibility(false);
  }

  // ── Lazy hydration ───────────────────────────────────────────────

  /**
   * Fetch /cart.js on page load to populate header cart count badges.
   */
  async _fetchInitialCartCount() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const cartJson = await res.json();
      this._updateCartUI(cartJson);
      this._tryRestorePersistedAddons(cartJson);
    } catch {
      // Non-critical — badge stays at 0 until next cart action
    }
  }

  /**
   * Hydrate the lazy drawer shell with full server-rendered HTML.
   * @returns {Promise<void>}
   */
  async _hydrateDrawer() {
    if (this._hydrated) return;
    if (this._hydratePromise) return this._hydratePromise;

    this._hydratePromise = (async () => {
      try {
        await this._refreshDrawerHTML();
        this._hydrated = true;
        this.element.removeAttribute('data-lazy-cart-drawer');
        this._initCartInstance();
      } catch (error) {
        console.error('[CartDrawer] Failed to hydrate lazy drawer:', error);
      } finally {
        this._hydratePromise = null;
      }
    })();

    return this._hydratePromise;
  }

  /**
   * Create the Cart class instance. Called once after DOM is ready.
   */
  _initCartInstance() {
    const itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer) this.cart = new Cart(itemsContainer, null);
  }

  // ── Addons ───────────────────────────────────────────────────────

  /**
   * Toggle visibility of the addon section.
   * @param {boolean} visible
   */
  _setAddonVisibility(visible) {
    const section = this.element.querySelector(ADDON_SECTION_SEL);
    if (!section) return;
    section.classList.toggle("hidden", !visible);
    section.setAttribute("aria-hidden", String(!visible));
  }

  /**
   * After reload (or lazy hydrate), repopulate add-ons when cart has items and
   * the anchor PDP product is still in the cart.
   * @param {object|null|undefined} cartData
   */
  _tryRestorePersistedAddons(cartData) {
    if (!cartData) return;
    if (cartData.item_count === 0) {
      clearPersistedAddonContext();
      return;
    }

    const container = this.element.querySelector(ADDON_CONTAINER_SEL);
    if (!container || container.querySelector("[data-product-addon]")) return;

    const ctx = readPersistedAddonContext();
    if (!ctx || !addonContextRenderable(ctx)) {
      if (ctx && !addonContextRenderable(ctx)) clearPersistedAddonContext();
      return;
    }

    if (!persistedContextAppliesToCart(ctx, cartData)) {
      clearPersistedAddonContext();
      return;
    }

    void this._renderAddonsFromContext(ctx);
  }

  /**
   * Show checkout CTA only when cart has at least one item.
   * @param {boolean} hasItems
   */
  _syncCheckoutVisibility(hasItems) {
    this.element.querySelectorAll(CHECKOUT_SLOT_SEL).forEach((slot) => {
      slot.classList.toggle("hidden", !hasItems);
      slot.setAttribute("aria-hidden", String(!hasItems));
    });
  }

  /**
   * Fetch and render addon rows. Stores context for re-render on next open.
   * @param {Object|null} addonContext
   */
  async _renderAddonsFromContext(addonContext) {
    this._pendingAddonContext = addonContext;

    const section = this.element.querySelector(ADDON_SECTION_SEL);
    const container = this.element.querySelector(ADDON_CONTAINER_SEL);
    if (!section || !container) return;

    if (!addonContextRenderable(addonContext)) {
      // No curated list, no tag, no productId — nothing any engine could act on.
      console.warn('[CartDrawer] Add-ons hidden: context carries no usable source', addonContext);
      container.innerHTML = '';
      this._setAddonVisibility(false);
      return;
    }

    const renderGen = ++this._addonRenderGen;
    try {
      // Same source the PDP used: a curated list rides in the context (metafields), otherwise re-fetch
      // from the active engine.
      let raw = addonContext.products;
      if (!Array.isArray(raw) || !raw.length) {
        let fetchRecommendations;
        try {
          fetchRecommendations = (await import(ADDON_ENGINE)).fetchRecommendations;
        } catch (error) {
          // Distinct from a failed fetch: the engine module itself can't be resolved.
          throw new Error(
            `engine "${ADDON_ENGINE}" has no importmap entry, so it can't be loaded. Searchspring's ` +
              'entries are gated on the "Enable SearchSpring" and "SearchSpring site ID" theme settings — ' +
              'both must be set for snippets/scripts.liquid to emit them.',
            { cause: error },
          );
        }
        raw = await fetchRecommendations(addonContext);
      }
      const products = sanitizeAddonProductList(raw);
      if (renderGen !== this._addonRenderGen) return;

      const skuMap =
        addonContext.skuMap && typeof addonContext.skuMap === "object" ? addonContext.skuMap : null;

      if (!products.length) {
        // Nothing sellable to show — commonly a blank recommendations tag, or every candidate filtered
        // out as out-of-stock/preorder. Log the context so which one is obvious.
        console.warn('[CartDrawer] Add-ons hidden: no sellable products for', addonContext);
        container.innerHTML = '';
        this._setAddonVisibility(false);
        return;
      }

      container.innerHTML = buildAddonListHtml(
        products,
        addonContext.selectedOption || "",
        skuMap,
        { showSwatches: getProductAddonsShowSwatchesSetting() },
      );
      this._setAddonVisibility(true);
      persistAddonContext(addonContext);
    } catch (error) {
      if (renderGen !== this._addonRenderGen) return;
      console.error('[CartDrawer] Add-ons hidden:', error.message, error.cause ?? error);
      container.innerHTML = '';
      this._setAddonVisibility(false);
      return;
    }

    if (!section.dataset.addonsWired) {
      section.dataset.addonsWired = "true";
      section.addEventListener("click", (e) => this._onCartDrawerAddonsClick(e));
    }
  }

  /**
   * Swatch clicks + add-to-cart on addon rows in the drawer.
   * @param {MouseEvent} e
   */
  _onCartDrawerAddonsClick(e) {
    const swatch = e.target.closest("[data-product-addon-swatch]");
    if (swatch && this.element.contains(swatch)) {
      const row = swatch.closest("[data-product-addon]");
      if (!row || !this.element.contains(row)) return;
      const product = parseProductFromAddonRow(row);
      if (!product) return;
      const position = Number(swatch.dataset.optionPosition);
      const value = swatch.dataset.optionValue;
      if (!value || !Number.isFinite(position) || position < 1 || position > 3) return;
      if (swatch.getAttribute("aria-current") === "true") return;
      if (swatch.getAttribute("aria-disabled") === "true" || swatch.classList.contains("cursor-not-allowed")) return;
      const addBtn = row.querySelector("[data-product-addon-add]");
      const variant = resolveAddonVariant(product, addBtn?.dataset.variantId, position, value);
      if (variant) applyAddonVariantToRow(row, product, variant);
      return;
    }
    void this._onAddonAddClick(e);
  }

  /**
   * Handle add-to-cart clicks on addon buttons inside the drawer.
   * @param {MouseEvent} e
   */
  async _onAddonAddClick(e) {
    const btn = e.target.closest("[data-product-addon-add]");
    if (!btn || btn.disabled) return;
    e.preventDefault();

    const variantId = Number(btn.dataset.variantId);
    if (!variantId) return;

    document.dispatchEvent(new CustomEvent(CART_EVENTS.addStart, { detail: { variantId } }));

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = "Adding...";

    try {
      await AddToCart.addWithoutOpeningDrawer({ variantId, quantity: 1 });
      btn.textContent = "Added!";
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
    } catch {
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  // ── Layout helpers ───────────────────────────────────────────────

  /**
   * Sticky vs static summary based on height ratio.
   */
  _updateStickyState() {
    const content = this.element.querySelector(SCROLL_AREA_SEL);
    const summary = this.element.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      this.element.setAttribute("data-cart-summary-sticky", "false");
      return;
    }

    const ratio = summary.getBoundingClientRect().height / this.element.getBoundingClientRect().height;
    this.element.setAttribute("data-cart-summary-sticky", ratio > this._summaryThreshold ? "false" : "true");
  }

  /**
   * Close drawer when Shopify installments CTA is clicked.
   */
  _setupInstallmentsClose() {
    if (typeof customElements === "undefined") return;

    customElements
      .whenDefined("shopify-payment-terms")
      .then(() => {
        const cta = document.querySelector("shopify-payment-terms")?.shadowRoot?.querySelector("#shopify-installments-cta");
        cta?.addEventListener("click", () => this.close(), { once: true });
      })
      .catch(() => {});
  }

  // ── UI updates ───────────────────────────────────────────────────

  /**
   * Update cart count badges and subtotal.
   * @param {Object} cartData
   */
  _updateCartUI(cartData) {
    if (!cartData) return;

    const subtotalEl = this.element.querySelector("[data-cart-drawer-subtotal]");
    if (subtotalEl) subtotalEl.textContent = formatMoney(cartData.items_subtotal_price || 0);
    this._syncCheckoutVisibility((cartData.item_count || 0) > 0);

    for (const btn of document.querySelectorAll("[data-cart-button]")) {
      const count = btn.querySelector("[data-cart-count]");
      if (count) count.textContent = cartData.item_count || 0;
    }
  }

  /**
   * Ensure [data-cart-items] container exists (creates scaffold for lazy shells).
   * @returns {HTMLElement|null}
   */
  _ensureItemsContainer() {
    let itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer) return itemsContainer;

    const contentContainer = this.element.querySelector(ITEMS_CONTAINER_SEL);
    if (!contentContainer) return null;

    const scrollWrapper = document.createElement("div");
    scrollWrapper.className = "cart-drawer__content flex flex-1 flex-col overflow-y-auto";

    const innerWrapper = document.createElement("div");
    innerWrapper.className = "flex flex-col gap-4 px-4";

    itemsContainer = document.createElement("div");
    itemsContainer.className = "cart-drawer-items";
    itemsContainer.setAttribute("data-cart-items", "");

    innerWrapper.appendChild(itemsContainer);
    scrollWrapper.appendChild(innerWrapper);
    contentContainer.appendChild(scrollWrapper);

    this.element.setAttribute("data-has-items", "true");
    this._syncCheckoutVisibility(true);
    return itemsContainer;
  }

  // ── Optimistic UI ────────────────────────────────────────────────

  /**
   * Show a skeleton placeholder for the item being added, without touching
   * any already-rendered row — mirrors {@link _showOptimisticItem}'s
   * leave-existing-rows-alone behavior for callers with no product data
   * to render a real optimistic preview from (e.g. addon-drawer adds).
   * @param {number} variantId
   */
  _showSkeleton(variantId) {
    const itemsContainer = this._ensureItemsContainer();
    if (!itemsContainer) return;

    if (itemsContainer.classList.contains("hidden")) {
      itemsContainer.classList.remove("hidden");
      this.element.setAttribute("data-has-items", "true");
      this._syncCheckoutVisibility(true);
    }

    const existingItem = itemsContainer.querySelector(`[data-item-id="${variantId}"]`);
    if (existingItem) {
      existingItem.classList.add("animate-pulse");
      setTimeout(() => existingItem.classList.remove("animate-pulse"), 2000);
      return;
    }

    // Same structural classes as the real row in cart-drawer-items.liquid so
    // image/text/control column widths and heights match exactly — only the
    // content underneath is swapped for gray blocks.
    const row = document.createElement("div");
    row.className = "group/cart-item pt-4 sm:pb-4 pb-5 border-b last:border-b-0";
    row.dataset.itemId = String(variantId);
    row.setAttribute("data-optimistic", "true");
    row.setAttribute("data-cart-item", "");
    row.innerHTML = `
      <div class="flex flex-row gap-4 animate-pulse">
        <div class="flex flex-col shrink-0 max-sm:order-2">
          <div class="cart-item--image h-52 w-36 md:h-48 md:w-52 rounded bg-gray-200"></div>
        </div>
        <div class="max-sm:flex max-sm:flex-1 max-sm:flex-col max-sm:gap-2 sm:contents">
          <div class="relative flex flex-1 flex-col gap-2 sm:order-1">
            <div class="h-5 w-3/4 max-w-56 rounded bg-gray-200"></div>
            <div class="h-4 w-24 rounded bg-gray-200"></div>
            <div class="h-6 w-20 rounded bg-gray-200"></div>
          </div>
          <div class="sm:order-2 sm:pr-2 pt-1 sm:pt-2">
            <div class="h-10 w-24 rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    `;
    itemsContainer.insertAdjacentElement("afterbegin", row);
  }

  /**
   * Show an optimistic preview of the item being added.
   * @param {Object} data - Optimistic data from AddToCart.#collectOptimisticData
   */
  _showOptimisticItem(data) {
    const itemsContainer = this._ensureItemsContainer();
    if (!itemsContainer) return;

    if (itemsContainer.classList.contains("hidden")) {
      itemsContainer.classList.remove("hidden");
      this.element.setAttribute("data-has-items", "true");
      this._syncCheckoutVisibility(true);
    }

    const existingItem = itemsContainer.querySelector(`[data-item-id="${data.variantId}"]`);
    if (existingItem) {
      existingItem.classList.add("animate-pulse");
      setTimeout(() => existingItem.classList.remove("animate-pulse"), 2000);
      this._optimisticallyUpdateTotals(data.price, data.quantity);
      return;
    }

    itemsContainer.insertAdjacentHTML("afterbegin", this._buildOptimisticItemHTML(data));
    this._optimisticallyUpdateTotals(data.price, data.quantity);
  }

  /**
   * Build HTML for an optimistic cart item matching cart-drawer-items.liquid structure.
   * @param {Object} data
   * @returns {string}
   */
  _buildOptimisticItemHTML(data) {
    const title = escapeHtml(data.productTitle);
    const url = escapeHtml(data.productUrl);
    const imgSrc = escapeHtml(data.imageUrl);
    const linePrice = data.price * data.quantity;

    const optionsHtml =
      data.options.length > 0 && data.variantTitle !== "Default Title"
        ? `<div class="flex flex-col gap-2 p-0! text-xs font-medium tracking-wider text-gray-550 md:flex-row md:gap-4 md:text-sm">
          ${data.options.map((opt) => `<div>${escapeHtml(String(opt))}</div>`).join("")}
        </div>`
        : "";

    return `<div
      class="group/cart-item pt-4 sm:pb-4 pb-5 border-b last:border-b-0 animate-pulse"
      data-item-id="${data.variantId}"
      data-optimistic="true"
      data-cart-item
    >
      <div class="flex flex-row gap-4">
        <div class="flex flex-col shrink-0 max-sm:order-2">
          <a href="${url}" class="cart-item--image block h-52 w-36 md:h-48 md:w-52 overflow-hidden">
            ${imgSrc
              ? `<img src="${imgSrc}" alt="${title}" class="h-full w-full object-cover object-center" width="200" height="200" loading="eager">`
              : `<div class="h-full w-full rounded bg-gray-200"></div>`}
          </a>
        </div>
        <div class="max-sm:flex max-sm:flex-1 max-sm:flex-col max-sm:gap-2 sm:contents">
          <div class="relative flex flex-1 flex-col gap-2 sm:order-1">
            <div class="flex justify-between items-start gap-3">
              <a href="${url}" class="text-base font-bold md:text-lg tracking-wide min-w-0">
                ${title}
              </a>
            </div>
            <div>${optionsHtml}</div>
            <div class="flex justify-between items-end gap-3 flex-wrap">
              <div class="group/price" data-display-type="small">
                <div class="self-end text-xl font-semibold md:text-2xl text-price-color not-sale__price">
                  <span>${formatMoney(linePrice)}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-row sm:flex-col sm:self-stretch justify-between sm:order-2 sm:pr-2 pt-1 sm:pt-2">
            <div class="flex flex-row sm:flex-col sm:h-5/6 sm:justify-between w-full max-sm:contents">
              <div class="h-10 w-24 rounded bg-gray-200"></div>
              <div class="h-5 w-16 rounded bg-gray-200 max-sm:mr-8"></div>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  /**
   * Optimistically update subtotal and cart count badges.
   * @param {number} priceCents
   * @param {number} quantity
   */
  _optimisticallyUpdateTotals(priceCents, quantity) {
    const subtotalEl = this.element.querySelector("[data-cart-drawer-subtotal]");
    if (subtotalEl) {
      const rawText = subtotalEl.textContent.replace(/[^0-9.]/g, "");
      const currentCents = Math.round(parseFloat(rawText) * 100) || 0;
      subtotalEl.textContent = formatMoney(currentCents + priceCents * quantity);
    }

    for (const btn of document.querySelectorAll("[data-cart-button]")) {
      const count = btn.querySelector("[data-cart-count]");
      if (count) count.textContent = (parseInt(count.textContent, 10) || 0) + quantity;
    }
  }

  /**
   * Remove an optimistic item from the drawer (used on add-to-cart failure).
   * @param {number} variantId
   */
  _removeOptimisticItem(variantId) {
    if (!variantId) return;
    this.element.querySelector(`[data-optimistic="true"][data-item-id="${variantId}"]`)?.remove();

    const itemsContainer = this.element.querySelector(CART_ITEMS_SEL);
    if (itemsContainer && !itemsContainer.querySelector("[data-cart-item]")) {
      itemsContainer.classList.add("hidden");
      this.element.setAttribute("data-has-items", "false");
      this._syncCheckoutVisibility(false);
    }
  }

  // ── Server refresh ───────────────────────────────────────────────

  /**
   * Refresh cart drawer HTML by fetching updated content from server.
   * Morphs [cart-items-container] while preserving the live addon section.
   *
   * @param {Object|null} cartData - Cart JSON from #syncCart (authoritative),
   *   or null for hydration / removal refreshes.
   * @param {boolean} isRetry - Internal: set on the single retry after a
   *   failed fetch, so a retry can't recurse.
   */
  async _refreshDrawerHTML(cartData = null, isRetry = false) {
    const gen = ++this._refreshGen;
    const contentContainer = this.element.querySelector(ITEMS_CONTAINER_SEL);
    const isAuthoritativeRefresh = cartData != null;
    let fetchFailed = false;

    try {
      const url = `${window.Shopify?.routes?.root || "/"}cart?view=ajax-drawer&_=${Date.now()}`;
      const response = await fetch(url);
      if (!response.ok) {
        fetchFailed = true;
        return;
      }

      // Stale guard: skip if a newer refresh was queued, or if an add is
      // in-flight and this is a non-authoritative refresh (e.g. hydration).
      if (gen !== this._refreshGen || (this._addInFlight && !cartData)) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newCartDrawer = doc.querySelector("[data-cart-drawer]");
      const newContentContainer = newCartDrawer?.querySelector(ITEMS_CONTAINER_SEL);

      // ── Preserve live addon section across morphs ──────────────
      // The server HTML always renders addons empty/hidden. If we already
      // have a populated addon section, detach it before morph and
      // reattach after. On first add (empty→populated) let the server HTML
      // provide the section, then trigger the initial addon fetch.
      const liveAddonSection = contentContainer?.querySelector(ADDON_SECTION_SEL);
      const hasLiveAddons = !!liveAddonSection;

      if (hasLiveAddons) {
        liveAddonSection.remove();
        newContentContainer?.querySelector(ADDON_SECTION_SEL)?.remove();
      }

      // ── Morph ─────────────────────────────────────────────────
      if (contentContainer && newContentContainer) {
        // Remove items mid-animation before morphing. Cart items have no `id`
        // so morph matches them positionally — it would reuse the removing
        // element's node for the next item, strip `data-removing`, and cause
        // the item to flash back into view before its content is replaced.
        for (const el of contentContainer.querySelectorAll('[data-removing]')) {
          el.remove();
        }
        morph(contentContainer, newContentContainer, MORPH_OPTIONS);
      } else if (!contentContainer && newContentContainer) {
        this.element.querySelector("form")?.appendChild(newContentContainer);
      }

      // ── Reattach or init addons ───────────────────────────────
      if (hasLiveAddons) {
        const scrollArea = this.element.querySelector(SCROLL_AREA_SEL);
        if (scrollArea) scrollArea.appendChild(liveAddonSection);
      } else if (addonContextRenderable(this._pendingAddonContext)) {
        void this._renderAddonsFromContext(this._pendingAddonContext);
      }

      // ── Sync state flags ──────────────────────────────────────
      const newHasItems = newCartDrawer?.getAttribute("data-has-items");
      if (typeof newHasItems === "string") {
        this.element.setAttribute("data-has-items", newHasItems);
        this._syncCheckoutVisibility(newHasItems === "true");
      }

      // ── Rebind Cart instance ──────────────────────────────────
      const newItemsContainer = this.element.querySelector(CART_ITEMS_SEL);
      if (!this.cart && newItemsContainer) {
        this.cart = new Cart(newItemsContainer, null);
      } else if (this.cart && newItemsContainer) {
        this.cart.itemsContainer = newItemsContainer;
        this.cart.items.clear();
        this.cart.setupItemListeners();
      }

      // ── Sync cart data ────────────────────────────────────────
      if (cartData) {
        if (this.cart) this.cart.cart = cartData;
      } else {
        const cartRes = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        });
        if (cartRes.ok) {
          const freshData = await cartRes.json();
          if (this.cart) this.cart.cart = freshData;
          document.dispatchEvent(new CustomEvent(CART_EVENTS.updated, { detail: freshData }));
        }
      }

      const cartSnapshot = cartData ?? this.cart?.cart ?? null;
      this._tryRestorePersistedAddons(cartSnapshot);
    } catch (error) {
      fetchFailed = true;
      console.error("Failed to refresh cart drawer HTML:", error);
    } finally {
      // The mutation already succeeded server-side, so a failed refresh
      // leaves the DOM's data-line indexes stale — unlocking now would let
      // the next edit hit the wrong line. Retry once before unlocking; the
      // retry's own finally handles unlock either way.
      // ponytail: single retry, no backoff — add backoff if flaky networks show up in logs.
      if (fetchFailed && !isRetry && gen === this._refreshGen) {
        void this._refreshDrawerHTML(cartData, true);
      } else {
        // Only the newest authoritative refresh should release add-to-cart state.
        if (isAuthoritativeRefresh && gen === this._refreshGen) {
          this._addInFlight = false;
        }

        // A quantity-change/remove request already succeeded server-side
        // before this refresh was dispatched (Cart.update() resolved first) —
        // a successful morph clears the loading state as a side effect, but
        // if this refresh bailed early or threw, nothing else ever would.
        // Skip if a newer refresh is in flight — its finally handles this.
        if (gen === this._refreshGen) {
          this.cart?._setAllItemsLoading(false);
        }
      }
    }
  }
}

const init = () => {
  const cartDrawer = document.querySelector("[data-cart-drawer]");
  if (cartDrawer) new CartDrawer(cartDrawer).load();

  // Go-to-cart link loading state — shows spinner while navigating to /cart
  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      const link = e.target.closest('[data-go-to-cart-link]');
      if (link) link.setAttribute('data-go-to-cart-loading', '');
    });
  }
};
init();
